import { filter, merge, tap } from 'rxjs';
import { store } from '@redux/store';
import { onDocumentKeyDown$ } from 'layout/redux/observables';
import uiSlice from '../redux/uiStateSlice';
import defaultModalService from '../services/modalService';
import Overlay from './overlay';

// Shared across every createModal() instance (the default layout modal and the ISM's own
// instance) so Escape only closes the topmost one when modals are stacked — see openModal/
// closeModal below.
const activeModalStack = [];

/**
 * Creates an independent modal instance: its own Subject-backed modalService pairing, its own
 * DOM container, and its own "which named modal is open" bookkeeping (via the injected
 * getModalInformation/clearModalInformation). Multiple instances can coexist on the same page
 * without fighting over a shared container, a shared "current modal name" Redux field, or a
 * shared backdrop element.
 *
 * @param {Object} params
 * @param {Object} params.modalService - The modalService instance this modal reacts to.
 * @param {Element} params.container - The DOM element whose innerHTML this modal controls.
 * @param {Function} params.getModalInformation - Returns this instance's current
 *   `{ name, previousName }` (or null/undefined).
 * @param {Function} params.clearModalInformation - Clears this instance's "current modal name" state.
 * @param {string} [params.overlayId] - Id for this instance's backdrop element.
 */
export default function createModal({
  modalService,
  container,
  getModalInformation,
  clearModalInformation,
  overlayId,
}) {
  const modal$ = modalService.getHtml();

  const modalHtml = {
    modalContainer: container,
    previousHtml: null,
    previousCallback: null,
    escapeSubscription: null,
  };

  const disconnectedCallbacks = new Map();
  const registeredCallbacks = new Map();
  const overlay = new Overlay(overlayId);

  const isEscapeKeyPressed = (event) => event.key === 'Escape';

  const isTabKeyPressed = (event) => event.key === 'Tab';

  const getFocusableElements = () => {
    const focusableTargets = `a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])`;
    const focusableElements = modalHtml.modalContainer.querySelectorAll(focusableTargets);
    return {
      first: focusableElements[0],
      last: focusableElements[focusableElements.length - 1],
    };
  };

  const handleTabNavigation = (event) => {
    const { first, last } = getFocusableElements();
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  const onEscapeCloseModal$ = onDocumentKeyDown$.pipe(
    filter(() => !!modalHtml.modalContainer),
    filter(() => modalHtml.modalContainer.dataset.modalExpanded === 'true'),
    filter(isEscapeKeyPressed),
  );

  const onTabNavigation$ = onDocumentKeyDown$.pipe(
    filter(() => !!modalHtml.modalContainer),
    filter(() => modalHtml.modalContainer.dataset.modalExpanded === 'true'),
    filter(isTabKeyPressed),
  );

  const onDisconnected = (callbackName) => {
    const callbacksDisconnected = disconnectedCallbacks.get(callbackName) ?? [];
    callbacksDisconnected.filter((fn) => typeof fn === 'function').forEach((fn) => fn());

    if (disconnectedCallbacks.has(callbackName)) {
      disconnectedCallbacks.delete(callbackName);
      registeredCallbacks.delete(callbackName);
    }
  };

  const closeModal = () => {
    const modalInformation = getModalInformation();
    const callbackName = modalInformation?.name;

    modalHtml.escapeSubscription?.unsubscribe();
    modalHtml.escapeSubscription = null;

    onDisconnected(callbackName);

    modalHtml.modalContainer.classList.remove('modal--expanded');
    modalHtml.modalContainer.removeAttribute('data-modal-expanded');

    const stackIndex = activeModalStack.indexOf(modalHtml);
    if (stackIndex !== -1) {
      activeModalStack.splice(stackIndex, 1);
    }

    overlay.remove();

    modalHtml.modalContainer.innerHTML = '';
    modalHtml.previousCallback = null;
    modalHtml.previousHtml = null;

    clearModalInformation();

    const event = new CustomEvent('modalClosed', {
      detail: { modalInformation },
    });

    setTimeout(() => {
      document.body.dispatchEvent(event);
    }, 100);
  };

  const disconnectCallback = (data) => {
    const { name, fn } = data;
    const existingCallbacks = disconnectedCallbacks.get(name) ?? [];
    disconnectedCallbacks.set(name, [fn, ...existingCallbacks]);
  };

  const openModal = (html) => {
    const modalInformation = getModalInformation();
    const callbackName = modalInformation?.name;
    const previousCallbackName = modalInformation?.previousName;

    const callbacksRegistered = registeredCallbacks.get(callbackName) ?? [];

    modalHtml.previousCallback = callbackName;
    modalHtml.previousHtml = modalHtml.modalContainer.innerHTML?.trim();

    if (modalHtml.previousHtml) {
      onDisconnected(previousCallbackName);
    }

    modalHtml.modalContainer.innerHTML = html.value;
    modalHtml.modalContainer.classList.add('modal--expanded');
    modalHtml.modalContainer.setAttribute('data-modal-expanded', 'true');

    overlay.create();

    const stackIndex = activeModalStack.indexOf(modalHtml);
    if (stackIndex !== -1) {
      activeModalStack.splice(stackIndex, 1);
    }
    activeModalStack.push(modalHtml);

    callbacksRegistered.filter((fn) => typeof fn === 'function').forEach((fn) => fn());

    modalHtml.escapeSubscription?.unsubscribe();
    modalHtml.escapeSubscription = onEscapeCloseModal$.subscribe(() => {
      if (activeModalStack[activeModalStack.length - 1] !== modalHtml) {
        return;
      }
      modalService.clearHtml();
    });
  };

  const registerCallback = (data) => {
    const { name, fn } = data;
    const existingCallbacks = registeredCallbacks.get(name) ?? [];
    registeredCallbacks.set(name, [fn, ...existingCallbacks]);
  };

  const setPreviousHtml = () => {
    onDisconnected(modalHtml.previousCallback);
    modalHtml.modalContainer.innerHTML = '';
    modalService.setHtml({ payload: modalHtml.previousHtml });
  };

  const close = modal$.pipe(
    filter((data) => data.action === 'clearHtml'),
    tap(closeModal),
  );

  const disconnect = modal$.pipe(
    filter((data) => data.action === 'onHtmlRemoved'),
    tap(disconnectCallback),
  );

  const open = modal$.pipe(
    filter((data) => data.action === 'setHtml'),
    tap(openModal),
  );

  const register = modal$.pipe(
    filter((data) => data.action === 'onHtmlReady'),
    tap(registerCallback),
  );

  const previous = modal$.pipe(
    filter((data) => data.action === 'setPreviousHtml'),
    tap(setPreviousHtml),
  );

  onTabNavigation$.subscribe(handleTabNavigation);

  merge(close, disconnect, open, register, previous).subscribe();
}

createModal({
  modalService: defaultModalService,
  container: document.querySelector('.modal__box'),
  getModalInformation: () => store.getState().uiState?.modalInformation,
  clearModalInformation: () => store.dispatch(uiSlice.actions.clearModal()),
  overlayId: 'overlayElement',
});
